/**
 * "Back to where you were" deep-link history for the Marketer View (SYR-68).
 *
 * The key UX requirement: exploring out of the Briefing must be trivially
 * reversible. When a click inside the Briefing deep-links the user to another
 * destination (e.g. a Q2 row → The Benchmarks), we record the
 * origin `{ path, label, scrollY }` and show a floating "Back to <label>" bar
 * on the destination that restores the exact route and scroll position.
 *
 * Navigating manually via the sidebar clears the saved return point (handled by
 * the sidebar calling `clearReturn`).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

interface ReturnPoint {
  /** Absolute path to return to (within the marketer shell). */
  path: string;
  /** Human label shown in the back bar, e.g. "The Briefing · Overview". */
  label: string;
  /** Scroll offset to restore. */
  scrollY: number;
}

interface NavReturnContextValue {
  returnPoint: ReturnPoint | null;
  /** Capture the current location as the return point, then navigate to `to`. */
  navigateWithReturn: (to: string, fromLabel: string) => void;
  /** Go back to the saved return point and restore scroll. */
  goBack: () => void;
  /** Clear the saved return point (call when navigating via the sidebar). */
  clearReturn: () => void;
}

const NavReturnContext = createContext<NavReturnContextValue | null>(null);

export const NavReturnProvider: React.FunctionComponent<{
  children: React.ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const [returnPoint, setReturnPoint] = useState<ReturnPoint | null>(null);

  const navigateWithReturn = useCallback(
    (to: string, fromLabel: string) => {
      setReturnPoint({
        path: window.location.pathname + window.location.search,
        label: fromLabel,
        scrollY: window.scrollY,
      });
      navigate(to);
      window.scrollTo({ top: 0 });
    },
    [navigate],
  );

  const goBack = useCallback(() => {
    if (!returnPoint) return;
    const { path, scrollY } = returnPoint;
    navigate(path);
    setReturnPoint(null);
    // The origin (e.g. The Briefing) refetches on remount, so it's short for a
    // moment after navigating — a single scrollTo lands at the top. Retry until
    // the page is tall enough and the offset sticks (or we give up after ~3s).
    if (scrollY <= 0) return;
    let tries = 0;
    const restore = (): void => {
      window.scrollTo({ top: scrollY });
      const reached = Math.abs(window.scrollY - scrollY) <= 2;
      if (!reached && tries++ < 40) {
        window.setTimeout(restore, 75);
      }
    };
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(restore),
    );
  }, [navigate, returnPoint]);

  const clearReturn = useCallback(() => setReturnPoint(null), []);

  const value = useMemo<NavReturnContextValue>(
    () => ({ returnPoint, navigateWithReturn, goBack, clearReturn }),
    [returnPoint, navigateWithReturn, goBack, clearReturn],
  );

  return (
    <NavReturnContext.Provider value={value}>
      {children}
    </NavReturnContext.Provider>
  );
};

export function useNavReturn(): NavReturnContextValue {
  const ctx = useContext(NavReturnContext);
  if (!ctx) {
    throw new Error('useNavReturn must be used within a NavReturnProvider');
  }
  return ctx;
}
